// Entry list with search + category filter.
//
// Two-phase render: `mountList` builds the toolbar once (so the search input
// keeps focus) and returns an `update` function that only re-renders rows.

import { el, clear } from './dom.js';
import { avatarFor } from '../util/avatar.js';

const TYPE_LABELS = {
  login: 'Login',
  card: 'Card',
  license: 'License',
  identity: 'Identity',
  note: 'Note',
  apiKey: 'API Key',
};

export function mountList(container, { initialSearch, onSearchChange, onNew }) {
  clear(container);

  const searchInput = el('input', {
    type: 'search',
    placeholder: 'Search…',
    value: initialSearch ?? '',
    autocomplete: 'off',
    spellcheck: false,
    oninput: (e) => onSearchChange(e.target.value),
  });

  const toolbar = el('div', { class: 'toolbar' }, [
    searchInput,
    el('button', { class: 'primary', onclick: onNew, title: 'New entry (⌘N)' }, ['+']),
  ]);

  const items = el('div', { class: 'items' });
  container.append(toolbar, items);

  function update({ entries, search, filter, selectedId, onSelect }) {
    // Don't touch the input — leave focus and caret intact.
    if (searchInput.value !== search) searchInput.value = search ?? '';

    const filtered = filterEntries(entries, search, filter);
    // Preserve scroll position across the rebuild: clearing the container
    // collapses its scrollHeight, so the browser resets scrollTop to 0. We
    // capture it before clearing and restore after (the browser clamps if the
    // new content is shorter, e.g. after a search narrows the results).
    const prevScroll = items.scrollTop;
    clear(items);
    if (filtered.length === 0) {
      items.append(el('div', { class: 'empty' }, [search ? 'No matches.' : 'No entries yet. Press “+” to add one.']));
      return;
    }
    for (const entry of filtered) {
      const row = el('div', {
        class: 'entry-row' + (entry.id === selectedId ? ' active' : ''),
        onclick: () => onSelect(entry.id),
      }, [
        entryIcon(entry, 24),
        el('div', { class: 'entry-row-text' }, [
          el('div', { class: 'title' }, [entry.title || '(untitled)']),
          el('div', { class: 'sub' }, [
            el('span', { class: 'badge' }, [TYPE_LABELS[entry.type] ?? entry.type]),
            subtitle(entry),
          ]),
        ]),
      ]);
      items.append(row);
    }
    items.scrollTop = prevScroll;
  }

  return {
    update,
    focusSearch: () => searchInput.focus(),
  };
}

function subtitle(e) {
  switch (e.type) {
    case 'login': return e.username || e.url || '';
    case 'card': return maskCard(e.number);
    case 'license': return e.product || '';
    case 'identity': return e.email || `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim();
    case 'apiKey': return e.service || '';
    default: return '';
  }
}

function maskCard(n) {
  if (!n) return '';
  const last4 = n.replace(/\s/g, '').slice(-4);
  return `•••• ${last4}`;
}

export function filterEntries(entries, search, filter) {
  let out = entries;
  if (filter === 'starred') out = out.filter((e) => e.starred);
  else if (filter && filter !== 'all') out = out.filter((e) => e.type === filter);
  if (search) {
    const q = search.toLowerCase();
    out = out.filter((e) => {
      // e.body is a Secure Note's entire content — omitting it meant notes were
      // searchable only by title. e.cardholder likewise for cards.
      const hay = [e.title, e.username, e.url, e.notes, e.body, e.product, e.service, e.cardholder, e.firstName, e.lastName, e.email]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }
  return [...out].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
}

// Pure index math for keyboard navigation. `current` is the index of the
// selected item (-1 if none). Returns the new index, clamped to [0, length-1]
// with no wrap-around; -1 when the list is empty. With no current selection,
// Down (delta +1) lands on the first item, Up (delta -1) on the last.
export function nextIndex(current, length, delta) {
  if (length <= 0) return -1;
  if (current < 0) return delta > 0 ? 0 : length - 1;
  return Math.min(length - 1, Math.max(0, current + delta));
}

export function countsByType(entries) {
  const counts = { all: entries.length, starred: 0, login: 0, card: 0, license: 0, identity: 0, note: 0, apiKey: 0 };
  for (const e of entries) {
    counts[e.type] = (counts[e.type] ?? 0) + 1;
    if (e.starred) counts.starred += 1;
  }
  return counts;
}

function entryIcon(entry, size) {
  if (typeof entry.iconDataUrl === 'string' && entry.iconDataUrl.length > 0) {
    return el('img', {
      class: 'entry-icon',
      src: entry.iconDataUrl,
      alt: '',
      width: size,
      height: size,
    });
  }
  const { initial, hue } = avatarFor(entry.title || '');
  return el('span', {
    class: 'entry-icon avatar',
    style: { background: `hsl(${hue} 60% 45%)`, width: `${size}px`, height: `${size}px` },
  }, [initial]);
}
